
import img0 from './Images/DownloadConfig.png';
import img1 from './Images/DownloadEncryptionConfig.png';
  
const content = `# Database Download Guide
## Objective
If you have backed up your databases to the OnData platform, you can download those backups as needed. The downloaded databases can be restored in your environment. The 
objective of the document is to provide details on how to download database backups from the OnData platform. Please note that only databases that were successfully backed up 
to the OnData platform before are made available for download.

Besides transferring the backup securely with HTTPS, three additional security mechanisms are used to provide more layers of security for database download. First, the device code 
flow process is used to ensure the person who downloads the database is an authorized user. Second, the IP address of the network where the download request is originated must be 
added to the IP whitelist of the customer-specific cloud storage account. Third, the database backups are fully encrypted before transfer. The encrypted backups can be decrypted 
once all files are successfully downloaded from OnData.

**Note:** As the data egress from the cloud platforms are not free, the database download activities will incur a cost on the data being transferred. The actual cost is proportional
to the amount of data being downloaded from OnData.

## Download Database Backups
Follow the steps below to download a database backup from OnData.
* Follow these steps to add the public IP address of your network to the IP whitelist of your account-specific cloud storage:  
a. Login to the OnData Portal  
b. Navigate to \`Settings\` > \`Organization\` > \`IP Whitelist\`   
c. Add your public IP address or IP address range to the whitelist  

* Download the DownloadConfig.ini file for the database backup from the "Backup and Validation Details" page of the OnData Portal.

  ![Config File for Download](${ img0 } 'Config File for Download')

* Download the PowerShell script "DownloadBackup.ps1" from the following link.

  [PowerShell Script for Downloading Backups](download_res#DownloadBackup/DownloadBackup.ps1 'PowerShell Script for Downloading Backups')

* Copy the "DownloadConfig.ini" file and the PowerShell script "DownloadBackup.ps1" to a machine that has access to the Internet.

  **NOTE:** Place the "DownloadConfig.ini" file and the "DownloadBackup.ps1" file in **the same folder**. Also, please make sure you have enough free disk space that is at
  least twice as large as the size of the full database backup.

* Launch a PowerShell command line with super user permission. Use sudo for Ubuntu and su for Redhat Linux and CentOS. Execute the DownloadBackup.ps1 file to download the backup.

  \`\`\`PowerShell
  $ sudo pwsh
  PS /home/user> ./DownloadBackup.ps1
  \`\`\`

* You will be prompted to perform authentication using the device code flow process. Follow the instructions from the PowerShell script to do the following:  
a. Launch a web browser and navigate to \`https://us.ondatahub.com/device\`  
b. Login with your account credentials  
c. Enter the device code to start the download process  

  **NOTE:** If the downloaded database backup is not a full database backup, you may need to download the corresponding full backup and all incremental backups in between
  in order to restore the database.

* If you do not want to download additional files, you may remove your public IP address or IP address range from the IP whitelist of your account-specific cloud storage

  
## Download Encrypted Database Backups
Leveraging the OnData platform, you can encrypt the sensitive data in a database backup and that encrypted database backup can be downloaded from the OnData platform as well. 
Follow the steps below to download an encrypted database backup from OnData.
* Follow these steps to add the public IP address of your network to the IP whitelist of your account-specific cloud storage:  
a. Login to the OnData Portal  
b. Navigate to \`Settings\` > \`Organization\` > \`IP Whitelist\`   
c. Add your public IP address or IP address range to the whitelist  

* Download the DownloadEncryptionConfig.ini file for the encrypted database backup from the "Encryption Details" page of the OnData Portal.

  ![Config File for Download](${ img1 } 'Config File for Download')

* Download the PowerShell script "DownloadEncryption.ps1" from the following link.

  [PowerShell Script for Downloading Encrypted Backups](download_res#DownloadEncryption/DownloadEncryption.ps1 'PowerShell Script for Downloading Encrypted Backups')

* Copy the "DownloadEncryptionConfig.ini" file and the PowerShell script "DownloadEncryption.ps1" to a machine that has access to the Internet.

  **NOTE:** Place the "DownloadEncryptionConfig.ini" file and the "DownloadEncryption.ps1" file in **the same folder**. Also, please make sure you have enough free disk
  space that is at least twice as large as the size of the encrypted database backup.

* Launch a PowerShell command line with super user permission. Use sudo for Ubuntu and su for Redhat Linux and CentOS. Execute the DownloadEncryption.ps1 file to
  download the backup.

  \`\`\`PowerShell
  $ sudo pwsh
  PS /home/user> ./DownloadEncryption.ps1
  \`\`\`

* You will be prompted to perform authentication using the device code flow process. Follow the instructions from the PowerShell script to do the following:  
a. Launch a web browser and navigate to \`https://us.ondatahub.com/device\`  
b. Login with your account credentials  
c. Enter the device code to start the download process  

  **NOTE:** The encrypted database can be restored after the download. To connect to the encrypted database with dynamic encryption and decryption enabled, you will need 
  to download and install the OnData database drivers. For more details about the OnData database drivers, please refer to the corresponding documentation under 
  the \`Database Drivers\` topic.

* If you do not want to download additional files, you may remove your public IP address or IP address range from the IP whitelist of your account-specific cloud storage
  
   
`
  
export default content;
